import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError, resolveImageUrl } from "@/lib/api";
import { useCategories } from "@/contexts/CategoriesContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Upload, GripVertical, Package } from "lucide-react";

import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const EMPTY = {
  slug: "", title: "", blurb: "", image_url: "",
  order: 0, active: true,
};

function ImageFieldInline({ value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or upload"
          className="flex-1 h-10"
        />
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" />
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-10">
          <Upload className="w-4 h-4 mr-2" /> {uploading ? "…" : "Upload"}
        </Button>
      </div>
      {value && <img src={resolveImageUrl(value)} alt="" className="h-24 w-full object-cover border border-border bg-secondary" />}
    </div>
  );
}

function SortableRow({ c, reordering, onEdit, onDelete, onToggleActive }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: c.category_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : "auto",
    background: isDragging ? "#f8fafc" : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`cat-row-${c.slug}`}
      className="p-4 border-b border-border last:border-b-0 md:grid md:grid-cols-12 md:items-center md:gap-3"
    >
      {/* MOBILE: compact horizontal layout */}
      <div className="flex items-center gap-3 md:hidden">
        <button
          type="button"
          {...attributes}
          {...listeners}
          disabled={reordering}
          aria-label="Drag to reorder"
          data-testid={`cat-drag-${c.slug}-m`}
          className="cursor-grab active:cursor-grabbing touch-none p-2 -ml-2 rounded hover:bg-secondary text-muted-foreground disabled:opacity-50 shrink-0"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <img
          src={resolveImageUrl(c.image_url) || "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'></svg>"}
          alt=""
          className="w-12 h-12 object-cover bg-secondary border border-border shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold truncate">{c.title}</div>
          <div className="text-xs text-muted-foreground font-mono truncate">/{c.slug}</div>
        </div>
        <span
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono border border-border bg-secondary shrink-0"
          title="Products in this category"
        >
          <Package className="w-3 h-3" />
          {c.product_count ?? 0}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3 mt-3 md:hidden">
        {c.blurb && <p className="text-xs text-muted-foreground line-clamp-2 flex-1">{c.blurb}</p>}
        <div className="flex items-center gap-2 shrink-0">
          <Switch
            checked={c.active}
            onCheckedChange={() => onToggleActive(c)}
          />
          <Button size="icon" variant="outline" onClick={() => onEdit(c)} className="h-8 w-8">
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => onDelete(c)} className="h-8 w-8">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* DESKTOP: 12-col grid */}
      <div className="hidden md:flex md:col-span-1 justify-center">
        <button
          type="button"
          {...attributes}
          {...listeners}
          disabled={reordering}
          aria-label="Drag to reorder"
          data-testid={`cat-drag-${c.slug}`}
          className="cursor-grab active:cursor-grabbing touch-none p-2 -m-2 rounded hover:bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </div>
      <img
        src={resolveImageUrl(c.image_url) || "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'></svg>"}
        alt=""
        className="hidden md:block md:col-span-1 w-12 h-12 object-cover bg-secondary border border-border"
      />
      <div className="hidden md:block md:col-span-3">
        <div className="font-display font-semibold">{c.title}</div>
        <div className="text-xs text-muted-foreground font-mono">/{c.slug}</div>
      </div>
      <div className="hidden md:block md:col-span-3 text-xs text-muted-foreground">{c.blurb || "—"}</div>
      <div className="hidden md:block md:col-span-1">
        <span
          data-testid={`cat-count-${c.slug}`}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono border border-border bg-secondary text-foreground"
          title="Products in this category"
        >
          <Package className="w-3 h-3" />
          {c.product_count ?? 0}
        </span>
      </div>
      <div className="hidden md:flex md:col-span-2 items-center gap-2">
        <Switch
          checked={c.active}
          onCheckedChange={() => onToggleActive(c)}
          data-testid={`cat-active-${c.slug}`}
        />
        <span className="text-xs text-muted-foreground">{c.active ? "Active" : "Hidden"}</span>
      </div>
      <div className="hidden md:flex md:col-span-1 justify-end gap-1">
        <Button size="icon" variant="outline" onClick={() => onEdit(c)} data-testid={`cat-edit-${c.slug}`} className="h-8 w-8">
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button size="icon" variant="ghost" onClick={() => onDelete(c)} data-testid={`cat-del-${c.slug}`} className="h-8 w-8">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function AdminCategories() {
  const { refresh: refreshPublic } = useCategories();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [reordering, setReordering] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadAdmin = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/categories");
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAdmin(); }, [loadAdmin]);

  const refreshAll = async () => {
    await Promise.all([loadAdmin(), refreshPublic()]);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, order: list.length });
    setOpen(true);
  };
  const startEdit = (c) => {
    setEditing(c);
    const { product_count, category_id, created_at, ...rest } = c;
    setForm({ ...rest });
    setOpen(true);
  };

  const save = async () => {
    setError(null);
    try {
      if (editing) {
        await api.put(`/admin/categories/${editing.category_id}`, form);
        toast.success("Category updated");
      } else {
        await api.post("/admin/categories", form);
        toast.success("Category created");
      }
      setOpen(false);
      await refreshAll();
    } catch (e) { setError(formatApiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    try {
      await api.delete(`/admin/categories/${c.category_id}`);
      toast.success("Deleted");
      await refreshAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (c) => {
    try {
      const { product_count, category_id, created_at, ...rest } = c;
      await api.put(`/admin/categories/${category_id}`, {
        ...rest, active: !c.active,
      });
      await refreshAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = list.findIndex((c) => c.category_id === active.id);
    const newIndex = list.findIndex((c) => c.category_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(list, oldIndex, newIndex);
    setList(next);
    setReordering(true);
    try {
      await api.post("/admin/categories/reorder", {
        ids: next.map((c) => c.category_id),
      });
      await refreshPublic();
    } catch (e) {
      toast.error(formatApiError(e));
      // Revert on failure
      await loadAdmin();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div data-testid="admin-categories" className="space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="overline">Homepage sections</div>
          <h2 className="font-display text-2xl font-semibold mt-1">Categories</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Drag <GripVertical className="w-3 h-3 inline -mt-1" /> to reorder. Counts show products linked by slug.
          </p>
        </div>
        <Button onClick={startNew} className="rounded-md" data-testid="category-new">
          <Plus className="w-4 h-4 mr-2" /> New category
        </Button>
      </div>

      {(() => {
        if (loading) {
          return (
            <div className="border border-border bg-white p-8 text-sm text-muted-foreground text-center">
              Loading categories…
            </div>
          );
        }
        if (list.length === 0) {
          return (
            <div className="border border-border bg-white p-8 text-sm text-muted-foreground text-center">
              No categories yet.
            </div>
          );
        }
        return (
          <div className="border border-border bg-white">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={list.map((c) => c.category_id)}
                strategy={verticalListSortingStrategy}
              >
                {list.map((c) => (
                  <SortableRow
                    key={c.category_id}
                    c={c}
                    reordering={reordering}
                    onEdit={startEdit}
                    onDelete={remove}
                    onToggleActive={toggleActive}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        );
      })()}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit category" : "New category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => update("title", e.target.value)} className="mt-2" data-testid="cat-form-title" />
            </div>
            <div>
              <Label>Slug (used in /catalog?category=…)</Label>
              <Input
                value={form.slug}
                onChange={(e) => update("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                className="mt-2 font-mono"
                disabled={!!editing}
                data-testid="cat-form-slug"
              />
            </div>
            <div>
              <Label>Short description (blurb)</Label>
              <Textarea rows={2} value={form.blurb} onChange={(e) => update("blurb", e.target.value)} className="mt-2" data-testid="cat-form-blurb" />
            </div>
            <div>
              <Label>Image</Label>
              <div className="mt-2"><ImageFieldInline value={form.image_url} onChange={(v) => update("image_url", v)} /></div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => update("active", v)} data-testid="cat-form-active" />
              <span className="text-sm">{form.active ? "Visible on site" : "Hidden"}</span>
            </div>
            {error && <div className="text-sm text-destructive">{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="cat-form-save">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
